'use client';

import { usePathname } from 'next/navigation';
import Script from 'next/script';
import React, { useEffect, useRef } from 'react';

import useCookies from '@/hooks/useCookies';
import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

declare global {
    interface Window {
        fbq?: ((...args: unknown[]) => void) & {
            callMethod?: (...args: unknown[]) => void;
            queue?: unknown[];
            loaded?: boolean;
            version?: string;
        };
        _fbq?: Window['fbq'];
    }
}

const META_PIXEL_NOSCRIPT_URL = 'https://www.facebook.com/tr';

/**
 * Loads the Meta Pixel base code (fbq init + PageView) when a Meta Pixel ID is configured for the
 * cinema and the visitor has consented to analysis cookies. Fires an additional PageView on
 * client-side route changes, since the SPA navigation after the initial load isn't a full page load.
 */
export default function MetaPixel(): React.JSX.Element | null {
    const domainConfiguration = useDomainConfiguration();
    const metaPixelId = domainConfiguration.websiteConfiguration?.metaPixelId?.trim();

    const { analysisCookiesPermissionState } = useCookies();

    // The consent cookie can only be read client-side; `useCookies()` reports its SSR default
    // (true) during hydration, which would otherwise mismatch a returning visitor's real,
    // previously-rejected value. Deferring to post-mount keeps the first client render aligned
    // with the server-rendered output (both null) and only reveals the pixel once the real
    // consent value is known.
    const isMounted = useIsMounted();

    const isEnabled = isMounted && !!metaPixelId && analysisCookiesPermissionState;

    const pathname = usePathname();
    const isInitialPageviewRef = useRef(true);

    useEffect(() => {
        if (!isEnabled || typeof window === 'undefined' || !window.fbq) {
            return;
        }

        // The base pixel snippet below already fires the first PageView on load.
        if (isInitialPageviewRef.current) {
            isInitialPageviewRef.current = false;
            return;
        }

        window.fbq('track', 'PageView');
    }, [isEnabled, pathname]);

    if (!isEnabled) {
        return null;
    }

    const initScript = `
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
        n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${metaPixelId}');
        fbq('track', 'PageView');
    `;

    return (
        <>
            <Script
                id="meta-pixel-base"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{ __html: initScript }}
            />
            <noscript>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                    height="1"
                    width="1"
                    alt=""
                    style={{ display: 'none' }}
                    src={`${META_PIXEL_NOSCRIPT_URL}?id=${metaPixelId}&ev=PageView&noscript=1`}
                />
            </noscript>
        </>
    );
}
